import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Truck, Boxes, TimerReset, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 4 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-pulse" />
              <span className="text-[11px] font-medium tracking-wider uppercase text-emerald-900">
                Certified Distributor Network
              </span>
            </div>
            <h1 className="font-display font-black text-slate-900 leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              Hospital-grade supply,<br />
              <span className="text-emerald-900">delivered direct.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 max-w-xl leading-relaxed">
              A curated catalog of professional-tier hospital consumables — PPE, sanitary,
              linen, wound care and diagnostics — shipped to your door by our vetted
              distributor partners. No touchpoints. No middlemen at receiving.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="home-cta-shop"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 transition-colors group"
              >
                Browse the catalog
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white border border-slate-300 text-slate-900 text-sm font-semibold rounded-sm hover:border-slate-900 transition-colors"
              >
                Request bulk pricing
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              <Stat value="24hr" label="Dispatch" />
              <Stat value="1,200+" label="SKUs" />
              <Stat value="99.98%" label="Order accuracy" />
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] max-w-lg mx-auto lg:ml-auto grain">
              <img
                src="https://images.unsplash.com/photo-1512678080530-7760d81faba6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Sterile hospital room"
                className="w-full h-full object-cover rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-white shadow-xl border border-slate-200 p-5 max-w-[240px] rounded-sm">
                <p className="overline text-emerald-800 mb-1">No-touch freight</p>
                <p className="text-sm text-slate-700 leading-snug">
                  Distributor ships direct — you pay a single professional invoice.
                </p>
              </div>
              <div className="absolute -top-6 -right-6 bg-slate-900 text-white p-5 max-w-[220px] rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span className="overline text-emerald-400">FDA / EPA</span>
                </div>
                <p className="text-sm leading-snug">
                  Every SKU sourced from certified, audited distributors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE / TRUST STRIP */}
      <section className="bg-slate-900 text-slate-400 overflow-hidden border-b border-slate-800">
        <div className="flex whitespace-nowrap marquee py-4">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-14 px-8">
              {["NIOSH", "FDA CLEARED", "EPA REGISTERED", "ASTM CERTIFIED", "AAMI LEVEL 3", "ISO 13485", "OSHA COMPLIANT", "HSA APPROVED"].map((x) => (
                <span key={x} className="overline text-slate-400">{x}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
          <div>
            <p className="overline text-emerald-800 mb-3">Categories</p>
            <h2 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
              Every supply your facility needs.
            </h2>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-emerald-900">
            View all products <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`home-category-${c.slug}`}
              className={`group relative overflow-hidden rounded-sm border border-slate-200 hover:border-emerald-900/40 transition-colors ${
                i === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
            >
              <div className={`relative ${i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-[4/3]"}`}>
                <img
                  src={c.image_url}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <p className="overline text-emerald-300 mb-1">Shop</p>
                  <p className={`font-display font-bold text-white leading-tight ${i === 0 ? "text-2xl md:text-3xl" : "text-lg"}`}>
                    {c.name}
                  </p>
                  {i === 0 && (
                    <p className="text-sm text-slate-200 mt-2 max-w-xs">{c.description}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="overline text-emerald-800 mb-3">Best sellers</p>
              <h2 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                Trusted by procurement teams.
              </h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-emerald-900">
              Full catalog <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="overline text-emerald-800 mb-3">How it works</p>
            <h2 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4">
              Simple. Sterile. Straight to you.
            </h2>
            <p className="text-slate-600">
              We aggregate best-in-class hospital supply distributors under one professional
              storefront. You place one order — the distributor picks, packs and ships it
              directly. No warehouse, no delay.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Boxes, title: "01 — Choose your supplies", body: "Browse our vetted catalog of 1,200+ hospital-grade SKUs, all inspected for quality and compliance." },
              { icon: ShieldCheck, title: "02 — Secure checkout", body: "Pay through encrypted Stripe checkout. Business net-terms available for verified accounts." },
              { icon: Truck, title: "03 — Distributor ships direct", body: "Your order is routed to the closest certified distributor and dispatched within 24 hours." },
              { icon: TimerReset, title: "04 — Track & reorder", body: "Real-time order tracking and one-click reorders in your account dashboard." },
            ].map((s, i) => (
              <div key={i} className="border border-slate-200 p-6 rounded-sm hover:border-emerald-900/40 transition-colors bg-white">
                <s.icon className="w-6 h-6 text-emerald-900 mb-4" />
                <p className="font-display font-bold text-slate-900 mb-2">{s.title}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-emerald-900 text-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-emerald-300 mb-3">For procurement teams</p>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">
              Need bulk pricing or a quarterly PO?
            </h2>
            <p className="mt-4 text-emerald-100 max-w-lg">
              Our team builds custom quote sheets for hospitals, clinics, care facilities, and
              home-health agencies. Volume pricing on 500+ unit orders.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link
              to="/contact"
              className="px-6 py-3.5 bg-white text-emerald-900 text-sm font-semibold rounded-sm hover:bg-emerald-50 transition-colors inline-flex items-center gap-2"
            >
              Request a quote <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/shop"
              className="px-6 py-3.5 border border-emerald-400/50 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 transition-colors"
            >
              Shop catalog
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-display text-2xl font-black text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
}
